import { NestedTreeControl } from '@angular/cdk/tree';
import { HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';

import { ApiService } from '@api/api.service';
import { ExportFolder } from '@api/types';
import { ExportServiceUpdate } from '@api/types/export_service_update';

import { RavIcon } from '@shared/icon/icon';
import { ModalService } from '@shared/modal/modal.service';
import { cloneDeep, orderBy } from 'lodash';

import { Observable, Subscription } from 'rxjs';

import { Connection } from 'src/app/main/account/connections/connections.component';

export interface FolderNode extends ExportFolder {
  oneDriveSkipToken?: string;
  type?: string;
}

@Component({
  selector: 'rav-connection-folder-select',
  templateUrl: './connection-folder-select.component.html'
})
export class ConnectionFolderSelectComponent implements OnInit, OnDestroy {
  @Input() currentConnectionService: Observable<Connection>;
  @Output() navigateBack: EventEmitter<void> = new EventEmitter();

  nestedDataSource: MatTreeNestedDataSource<FolderNode>;
  nestedTreeControl: NestedTreeControl<FolderNode>;
  RavIcon = RavIcon;
  selectedNodeId: string;
  loadingFolderIds: string[] = [];
  selectedNodeName: string;
  selectedNodeType: string;
  selectedFolderName: string;
  subscriptions: Subscription[] = [];
  currentConnection: Connection = new Connection();
  folders: FolderNode[] = [];
  errorFetchingFolders = false;
  errorFetchingFoldersMessage: string;
  isFetchingRootFolders = false;
  isFetchingChildrenFolders = false;
  isLoadingFolders = true;
  oneDriveSkipTokenForRootFolder: string;

  constructor(private modal: ModalService, private api: ApiService) {
    this.nestedDataSource = new MatTreeNestedDataSource();
    this.nestedTreeControl = new NestedTreeControl<ExportFolder>((node) => node.children);
    this.listenFoldersExpansion();
  }

  ngOnInit() {
    /**
     * Get the folders of the service from API.
     */
    this.subscriptions.push(
      this.currentConnectionService.subscribe((connection) => {
        this.currentConnection = cloneDeep(connection);
        if (!connection.service) return;
        this.getRootFolders().catch((error) => {
          this.errorFetchingFoldersMessage = error.error.message;
          this.errorFetchingFolders = true;
          this.isFetchingRootFolders = false;
          this.isLoadingFolders = false;
        });
      })
    );

    /**
     * Clean up the selected node and folders data every time the modal is closed.
     */
    this.subscriptions.push(
      this.modal.close.subscribe(() => {
        this.selectedNodeId = '';
        this.selectedNodeName = '';
        this.selectedNodeType = '';
        this.nestedDataSource.data = [];
        this.folders = [];
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Update the folder id for the service and pop up an success modal.
   *
   * @param id The id of the folder.
   */
  selectFolder(id: string) {
    const patchBody: ExportServiceUpdate = {
      folder_id: id,
      folder_name: this.selectedNodeName
    };
    this.currentConnection.folder_id = id;
    this.api.exportServices.byId.patch(this.currentConnection.id, patchBody).subscribe(() => {
      this.modal.close.next({
        modalId: 'selectFolder',
        id: this.selectedNodeId,
        name: this.selectedNodeName,
        type: this.selectedNodeType,
        service: this.currentConnection.service
      });
      this.modal.open.next({ modalId: 'success' });
    });
  }

  /**
   * Get the selected note id and name.
   *
   * @param node The tree node user selected.
   */
  selectFolderNode(node: FolderNode) {
    this.currentConnection.folder_id = '';
    this.selectedNodeId = node.id;
    this.selectedNodeName = node.name;
    this.selectedNodeType = node.type;
    this.selectedFolderName = this.selectedNodeName;
  }

  /**
   * Determine if the node has nested children
   * in order to load children on open the folder returns always true
   * except for evernote because doesn't have sub folders
   */
  hasNestedChild(): any {
    return (_: number, node: FolderNode) => this.currentConnection.service !== 'evernote';
  }

  navigateBackToConnectionPage() {
    this.oneDriveSkipTokenForRootFolder = null;
    this.navigateBack.emit();
  }

  /**
   * Returns the node name from the node passed as param
   * For dropbox remove the full path and returns the name only
   *
   * @param node FolderNode
   */
  getNodeName(node: FolderNode): string {
    let name = node.name;

    if (this.currentConnection.service === 'dropbox') {
      const nameParts = node.name.split('/');
      name = nameParts[nameParts.length - 1];
    }

    return name;
  }

  /**
   * Fetch the root folders via APi call and get the one drive token from response header to get additional folder if exist.
   */

  async getRootFolders(): Promise<void> {
    if (this.isFetchingRootFolders) return;

    this.isFetchingRootFolders = true;
    const response = await this.fetchFolders(null, this.oneDriveSkipTokenForRootFolder).toPromise();

    this.errorFetchingFolders = false;
    this.isFetchingRootFolders = false;
    this.isLoadingFolders = false;

    this.oneDriveSkipTokenForRootFolder = response.headers.get('x-onedrive-skip-token');
    this.folders = this.folders.concat(response.body);
    this.folders = orderBy(this.folders, (folder: FolderNode) => folder.name.toLowerCase(), 'asc');
    this.nestedDataSource.data = this.folders;
  }

  /**
   * Fetch the children folders via API call and get the one drive token from response header to get additional folder if exist.
   *
   * @param node The node to fetch children folder .
   */
  async getChildrenFolders(node: FolderNode): Promise<void> {
    // Don't send the same request for the same node at same time.
    if (this.loadingFolderIds.includes(node.id)) return;

    this.loadingFolderIds.push(node.id);

    const childrenResponse = await this.fetchFolders(node.id, node.oneDriveSkipToken).toPromise();
    node.children = node.children.concat(childrenResponse.body);
    node.children = orderBy(node.children, (childFolder: ExportFolder) => childFolder.name.toLowerCase(), 'asc');
    node.oneDriveSkipToken = childrenResponse.headers.get('x-onedrive-skip-token');

    // Setting the data again will reload the tree view
    this.nestedDataSource.data = [];
    this.nestedDataSource.data = this.folders;
    this.loadingFolderIds = this.loadingFolderIds.filter((id) => id !== node.id);
  }

  private listenFoldersExpansion() {
    this.subscriptions.push(
      this.nestedTreeControl.expansionModel.changed.subscribe(async (data) => {
        const node = data.added[0];

        // Once children are loaded this prevent to make the request again
        // since Evernote does not have sub folders prevents requests
        if (!node || !node.children || !(node.children.length <= 0) || this.currentConnection.service === 'evernote') {
          return;
        }

        await this.getChildrenFolders(node).catch(() => {
          // In theory this never should happen :p
          alert('Error getting folder children');
          this.loadingFolderIds = [];
        });
      })
    );
  }

  /**
   * Get the list of the folder;
   *
   * @param nodeId The parent id to fetch folder list of. If null, means it's root folder.
   * @param skipToken The skip token to used to fetch additional folders.
   */
  private fetchFolders(nodeId: string | null, skipToken: string): Observable<HttpResponse<FolderNode[]>> {
    return this.api.exportFolders.service.list.get(this.currentConnection.service, nodeId, skipToken);
  }
}
